/**
 * StockHomologationsTable - Tabela única de homologações (página e ficha do rastreador).
 *
 * RELEASE: 2026-10-11 v1.1.0 - Resultado como bolinha colorida + texto preto (padrão de pílula).
 * RELEASE: 2026-10-11 v1.0.0 - Versão inicial.
 */
import { ArrowRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { HomologationRow } from '@/hooks/useStockHomologations';
import { homologationResultLabel, homologationTypeLabel } from '@/lib/stock-homologation';
import { formatStockDateTime, stockStatusLabel } from '@/lib/stock-presentation';
import { cn } from '@/lib/utils';

interface Props {
  items: HomologationRow[];
  isLoading: boolean;
  /** Oculta a coluna do rastreador (ficha individual) */
  hideTracker?: boolean;
  /** Abre os detalhes somente leitura ao clicar na linha (listagem geral) */
  onRowClick?: (item: HomologationRow) => void;
  header?: (label: string, field?: 'created_at' | 'number' | 'result' | 'homologation_type') => React.ReactNode;
}

/** Bolinha do resultado: aprovado verde, demais vermelho (texto sempre em cor normal) */
const resultDotClass = (r: string) => (r === 'approved' ? 'bg-emerald-500' : 'bg-destructive');

export function StockHomologationsTable({ items, isLoading, hideTracker, onRowClick, header }: Props) {
  const h = (label: string, field?: Parameters<NonNullable<Props['header']>>[1]) => (header ? header(label, field) : label);
  return (
    <Table className="min-w-[900px]">
      <TableHeader>
        <TableRow>
          <TableHead>{h('Nº', 'number')}</TableHead>
          {!hideTracker && <TableHead>Rastreador</TableHead>}
          <TableHead>{h('Tipo', 'homologation_type')}</TableHead>
          <TableHead>{h('Resultado', 'result')}</TableHead>
          <TableHead>Situação</TableHead>
          <TableHead>Plataforma</TableHead>
          <TableHead>Responsável</TableHead>
          <TableHead>{h('Data', 'created_at')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading && Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
        ))}
        {!isLoading && items.length === 0 && (
          <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">Nenhuma homologação encontrada</TableCell></TableRow>
        )}
        {!isLoading && items.map((r) => (
          <TableRow
            key={r.id}
            title={r.notes ?? undefined}
            className={cn(onRowClick && 'cursor-pointer hover:bg-muted/40')}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
          >
            <TableCell className="font-mono text-xs font-medium">{r.number}</TableCell>
            {!hideTracker && (
              <TableCell>
                <p className="font-mono text-sm">{r.imei}</p>
                <p className="text-xs text-muted-foreground">{r.model_name ?? '—'}</p>
              </TableCell>
            )}
            <TableCell className="text-sm">{homologationTypeLabel(r.homologation_type)}</TableCell>
            <TableCell><span className="inline-flex items-center gap-2 text-sm"><span className={cn('h-2 w-2 shrink-0 rounded-full', resultDotClass(r.result))} />{homologationResultLabel(r.result)}</span></TableCell>
            <TableCell className="text-xs">
              {r.status_before === r.status_after
                ? <span className="text-muted-foreground">{stockStatusLabel('tracker', r.status_before)} (sem alteração)</span>
                : <span className="inline-flex items-center gap-1">{stockStatusLabel('tracker', r.status_before)}<ArrowRight className="h-3 w-3" /><b>{stockStatusLabel('tracker', r.status_after)}</b></span>}
            </TableCell>
            <TableCell className="text-sm">{r.platform_name ?? '—'}</TableCell>
            <TableCell className="text-sm">{r.created_by_name ?? '—'}</TableCell>
            <TableCell className="text-xs tabular-nums">{formatStockDateTime(r.created_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
